import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { MEDIA } from "../lib/media";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";

const CATEGORIES = [["All", ""], ["Face", "face"], ["Hair", "hair"], ["Body", "body"], ["Fragrance", "fragrance"], ["Wellness", "wellness"]];
const CONCERNS = ["Hydration", "Brightening", "Acne Care", "Anti-Ageing", "Sensitive Skin", "Repair", "Exfoliation", "Sun Protection"];
const SORTS = [["Featured", "featured"], ["Best Selling", "best-selling"], ["Newest", "newest"], ["Price: Low to High", "price-asc"], ["Price: High to Low", "price-desc"]];

export default function Shop() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [concern, setConcern] = useState("");
  const [category, setCategory] = useState("");
  const [availability, setAvailability] = useState(false);
  const [sort, setSort] = useState("featured");
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    setLoading(true);
    if (slug) {
      api.get(`/collections/${slug}`).then(r => { setMeta(r.data); setProducts(r.data.products || []); }).finally(() => setLoading(false));
    } else {
      const q = sp.get("q") || "";
      api.get(`/products${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(r => { setMeta(null); setProducts(r.data); }).finally(() => setLoading(false));
    }
  }, [slug, sp]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (category) list = list.filter(p => p.category === category);
    if (concern) list = list.filter(p => (p.concerns || []).includes(concern));
    if (availability) list = list.filter(p => (p.inventory?.available || 0) > 0);
    const s = {
      "price-asc": (a, b) => a.price - b.price,
      "price-desc": (a, b) => b.price - a.price,
      "newest": (a, b) => (b.is_new === true) - (a.is_new === true),
      "best-selling": (a, b) => (b.sold || 0) - (a.sold || 0),
      "featured": (a, b) => (b.is_bestseller === true) - (a.is_bestseller === true),
    }[sort];
    list.sort(s);
    return list;
  }, [products, category, concern, availability, sort]);

  const banner = meta?.banner || MEDIA.face;
  const title = meta?.title || "All Products";
  const subtitle = meta?.description || "Thoughtfully crafted skincare inspired by Meghalaya's untouched beauty.";

  return (
    <div>
      <section className="relative h-[42vh] min-h-[300px] overflow-hidden flex items-end">
        <img src={banner} alt={title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-forest/40" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 pb-12 w-full text-ivory">
          <Reveal><span className="eyebrow text-ivory/80">Shop</span><h1 className="serif text-5xl lg:text-6xl mt-3">{title}</h1><p className="text-ivory/80 max-w-xl mt-3">{subtitle}</p></Reveal>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
        {/* category chips */}
        {!slug && (
          <div className="flex flex-wrap gap-3 mb-8">
            {CATEGORIES.map(([label, val]) => (
              <button key={label} onClick={() => setCategory(val)} data-testid={`cat-${label.toLowerCase()}`}
                className={`px-5 py-2 text-xs uppercase tracking-[0.14em] border transition-colors ${category === val ? "bg-forest text-ivory border-forest" : "border-forest/25 text-forest/70 hover:border-forest"}`}>{label}</button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between border-y border-sage/30 py-4 mb-10">
          <button onClick={() => setDrawer(true)} className="flex items-center gap-2 text-xs uppercase tracking-[0.14em]" data-testid="filter-open">
            <SlidersHorizontal size={16} /> Filters
          </button>
          <span className="text-xs text-forest/50">{filtered.length} products</span>
          <div className="relative">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select"
              className="appearance-none bg-transparent text-xs uppercase tracking-[0.14em] pr-6 outline-none cursor-pointer">
              {SORTS.map(([l, v]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        <div className="grid lg:grid-cols-[220px_1fr] gap-10">
          {/* sidebar filters desktop */}
          <aside className="hidden lg:block">
            <FilterPanel {...{ concern, setConcern, availability, setAvailability, category, setCategory, showCat: !slug }} />
          </aside>

          <div>
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/5] shimmer" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-24 text-center"><p className="serif text-3xl text-forest/50">Nothing found in this forest.</p></div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12">
                {filtered.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* mobile filter drawer */}
      {drawer && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div className="absolute inset-0 bg-forest/40" onClick={() => setDrawer(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-xs bg-ivory p-6 overflow-auto">
            <div className="flex items-center justify-between mb-8"><span className="serif text-2xl">Filters</span><button onClick={() => setDrawer(false)} data-testid="filter-close"><X size={22} /></button></div>
            <FilterPanel {...{ concern, setConcern, availability, setAvailability, category, setCategory, showCat: !slug }} />
            <button onClick={() => setDrawer(false)} className="btn-primary w-full mt-8">Show {filtered.length} products</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterPanel({ concern, setConcern, availability, setAvailability, category, setCategory, showCat }) {
  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow text-forest/60 mb-4">Shop by Concern</div>
        <div className="flex flex-col gap-2.5">
          <button onClick={() => setConcern("")} className={`text-left text-sm ${!concern ? "text-forest font-medium" : "text-forest/55"}`}>All</button>
          {CONCERNS.map((c) => (
            <button key={c} onClick={() => setConcern(c === concern ? "" : c)} data-testid={`concern-${c.toLowerCase().replace(/ /g, "-")}`}
              className={`text-left text-sm transition-colors ${concern === c ? "text-forest font-medium" : "text-forest/55 hover:text-forest"}`}>{c}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="eyebrow text-forest/60 mb-4">Availability</div>
        <label className="flex items-center gap-2 text-sm text-forest/70 cursor-pointer">
          <input type="checkbox" checked={availability} onChange={(e) => setAvailability(e.target.checked)} data-testid="filter-instock" className="accent-forest" /> In stock only
        </label>
      </div>
    </div>
  );
}
